import { LogLevel, MetaSession, MetaUser } from '@grafana/faro-core';
import { getSurveyDevice, getUspDevice } from 'Shared/services/deviceService';
import { isInAndroidApp, isInIOSApp } from 'Shared/services/mobileAppMessenger';

type UspDeviceType = {
  is_mobile           : boolean,
  complete_device_name: string,
  form_factor         : string
}

enum DeviceType {
  iOS     = 'ios',
  Android = 'android',
  Web     = 'web'
}

/**
 * Gets the user device from the survey cookie
 */
const getUserDevice = (): UspDeviceType | undefined => {
  try {
    return JSON.parse(getSurveyDevice());
  } catch (error) {
    logError('getUserDevice', error);
    return {
      is_mobile           : false,
      complete_device_name: 'UNABLE_TO_RETRIEVE',
      form_factor         : 'UNABLE_TO_RETRIEVE'
    };
  }
};

/**
 * Gets the user device app
 */
const getUserDeviceApp = (): DeviceType => {
  if (isInIOSApp()) {
    return DeviceType.iOS;
  }

  if (isInAndroidApp()) {
    return DeviceType.Android;
  }

  return DeviceType.Web;
};

/**
 * Sets the user context for Faro
 */
export const setUserContext = (userId: string): void => {
  const userDevice = getUserDevice();

  // Also set on window for global access
  if (window.FaroSession) {
    window.FaroSession.setUser({
      id: userId,
      username: userId,
      attributes: {
        user_device_is_mobile: userDevice?.is_mobile ? 'true' : 'false',
        user_device_complete_device_name: userDevice?.complete_device_name || '',
        user_device_form_factor: userDevice?.form_factor || '',
        user_device_usp: getUspDevice() || '',
        user_device_app: getUserDeviceApp() || '',
      }
    });
  }
};

/**
 * Gets the Faro initialization state
 */
export const isFaroInitialized = (): boolean => {
  return typeof window.FaroSession?.setUser === 'function';
};

/**
 * Logs an error to Faro
 * @param message
 * @param data
 * @returns void
 */
export const logError = (message: string, data: any): void => {
  if (!process.env.FARO_KEY || !process.env.FARO_API_URL || !process.env.FARO_APP_NAME) {
    return;
  }

  const errorStackOrData = data?.stack ?? data?.message ?? data;

  if (window.FaroLogger) {
    window.FaroLogger.error(message, { data: errorStackOrData });
  } else if (typeof window.logFaroErrorBuffered === 'function') {
    // Fallback to the global buffered error logger
    window.logFaroErrorBuffered(message, errorStackOrData);
  }
};

/**
 * Gets the user context from Faro
 */
export const getUserContext = () => {
  return window.FaroSession?.getSession();
};

/**
 * Initializes Faro
 */
export const initFaroDependencies = () => {
  if (process.env.APP_ENV === 'e2e' || process.env.MOCK_API === 'on' || !process.env.FARO_KEY || !process.env.FARO_API_URL || !process.env.FARO_APP_NAME) {
    return;
  }

  // Import Faro SDK
  import('@grafana/faro-web-sdk').then(({ getWebInstrumentations, initializeFaro }) => {
    // Remove the TracingInstrumentation import to avoid the XMLHttpRequestInstrumentation error
    try {
      const faro = initializeFaro({
        url: process.env.FARO_API_URL,
        apiKey: process.env.FARO_KEY,
        app: {
          name: process.env.FARO_APP_NAME,
          version: 'v.' + process.env.LD,
          environment: process.env.APP_ENV
        },
        instrumentations: [
          // Only use the default web instrumentations without tracing
          ...getWebInstrumentations({
            enablePerformanceInstrumentation: true,
          }),
          // Removed TracingInstrumentation to avoid XMLHttpRequestInstrumentation error
          // This can be re-enabled when Faro is updated to a compatible version
        ],
        ignoreUrls: [
          /.*smartlook.*/i,
          /.*localhost.*/i,
          /.*braze.*/i,
          /.*chrome-extension.*/i,
          /.*csp_report.*/i,
          /.*googletagmanager.*/i,
          /.*fontawesome.*/i,
          /.*clarity\.ms.*/i,
          /.*yofi.*/i,
          /.*firebaseremoteconfig.*/i,
          /.*roducine.*/i,
        ],
      });

      // Store Faro instances in module variables
      const faroLogger = faro.api.pushLog;

      // Also make Faro available globally
      // Create wrapper objects that match our global interface
      window.FaroSession = {
        setUser: (user: MetaUser) => faro.api.setUser(user),
        getSession: (): MetaSession | undefined => faro.api.getSession()
      };

      window.FaroLogger = {
        log: (message, context) => faroLogger([message], { level: LogLevel.LOG, context }),
        debug: (message, context) => faroLogger([message], { level: LogLevel.DEBUG, context }),
        info: (message, context) => faroLogger([message], { level: LogLevel.INFO, context }),
        warn: (message, context) => faroLogger([message], { level: LogLevel.WARN, context }),
        error: (message, context) => faroLogger([message], { level: LogLevel.ERROR, context })
      };

      // Flush any buffered errors
      if (typeof window.flushFaroErrorBuffer === 'function') {
        window.flushFaroErrorBuffer();
      }
    } catch (error) {
      // Log the error but don't break the application
      logError('Faro initialization failed', error);
    }
  }).catch((error) => {
    // Handle import errors gracefully
    logError('Faro import failed', error);
  });
};